import { PageLoader } from '@/components/ui/loader';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Target, Trophy } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/misc';
import { Avatar } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ApiRequestError } from '@/lib/api';
import { formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import { formatDuration, QUOTA_METRICS, useActivityReport, useQuotas, useSaveQuotas } from './api';

const monthKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

function months(): { key: string; label: string }[] {
  const now = new Date();
  return Array.from({ length: 13 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - 6 + index, 1);
    return { key: monthKey(date), label: date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) };
  }).reverse();
}

const METRIC_LABEL = Object.fromEntries(QUOTA_METRICS) as Record<string, string>;

/**
 * The activity report and goals (AmoCRM's and HubSpot's): what each person
 * did this month, how close they are to target, and who leads.
 */
export function ActivityPage() {
  const options = useMemo(months, []);
  const [period, setPeriod] = useState(monthKey(new Date()));
  const { data, isLoading } = useActivityReport(period);
  const [settingTargets, setSettingTargets] = useState(false);
  const rows = data?.rows ?? [];
  const leader = rows.find((row) => Number(row.revenue) > 0);

  return (
    <div>
      <PageHeader
        title="Activity & goals"
        description="Calls, emails, meetings, tasks and deals won per person, against the targets set for the month."
        breadcrumbs={[{ label: 'Sales hub' }, { label: 'Activity & goals' }]}
        actions={
          <div className="flex gap-2">
            <Select className="w-48" value={period} onChange={(event) => setPeriod(event.target.value)}>
              {options.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </Select>
            {data?.canSetQuotas && (
              <Button variant="outline" onClick={() => setSettingTargets(true)}>
                <Target /> Set targets
              </Button>
            )}
          </div>
        }
      />

      {leader && (
        <Card className="mb-5 border-amber-200 bg-amber-50/50">
          <CardContent className="flex items-center gap-3 pt-5">
            <Trophy className="h-8 w-8 text-amber-500" />
            <div>
              <p className="font-semibold">{leader.user.name} leads the month</p>
              <p className="text-sm text-muted-foreground">
                {formatMoney(leader.revenue, data?.currency)} won across {leader.dealsWon} deal{leader.dealsWon === 1 ? '' : 's'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {rows.some((row) => row.goals.length) && (
        <div className="mb-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows
            .filter((row) => row.goals.length)
            .map((row) => (
              <Card key={row.user.id}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Avatar name={row.user.name} size={24} /> {row.user.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2.5">
                  {row.goals.map((goal) => (
                    <div key={goal.metric}>
                      <div className="flex justify-between text-xs">
                        <span>{METRIC_LABEL[goal.metric]}</span>
                        <span className="text-muted-foreground">
                          {goal.metric === 'revenue' ? formatMoney(goal.actual, data?.currency) : goal.actual} /{' '}
                          {goal.metric === 'revenue' ? formatMoney(goal.target, data?.currency) : goal.target}
                        </span>
                      </div>
                      <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                        <div
                          className={cn('h-full rounded-full', goal.progress >= 100 ? 'bg-emerald-500' : 'bg-aurora')}
                          style={{ width: `${Math.min(100, goal.progress)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {isLoading ? (
            <PageLoader />
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No one to report on.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead className="text-end">Calls</TableHead>
                  <TableHead className="text-end">Talk time</TableHead>
                  <TableHead className="text-end">Emails</TableHead>
                  <TableHead className="text-end">Meetings</TableHead>
                  <TableHead className="text-end">Tasks done</TableHead>
                  <TableHead className="text-end">Notes</TableHead>
                  <TableHead className="text-end">New leads</TableHead>
                  <TableHead className="text-end">Won</TableHead>
                  <TableHead className="text-end">Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.user.id}>
                    <TableCell>
                      <span className="flex items-center gap-2 font-medium">
                        <span className="w-4 text-xs text-muted-foreground">{index + 1}</span>
                        <Avatar name={row.user.name} size={24} />
                        {row.user.name}
                      </span>
                    </TableCell>
                    <TableCell className="text-end">
                      {row.calls}
                      {row.calls > 0 && <span className="block text-xs text-muted-foreground">{row.callsConnected} connected</span>}
                    </TableCell>
                    <TableCell className="text-end">{formatDuration(row.talkSeconds)}</TableCell>
                    <TableCell className="text-end">
                      {row.emails}
                      {row.emails > 0 && <span className="block text-xs text-muted-foreground">{row.emailsOpened} opened</span>}
                    </TableCell>
                    <TableCell className="text-end">
                      {row.meetingsHeld}
                      {row.meetingsBooked > 0 && <span className="block text-xs text-muted-foreground">{row.meetingsBooked} booked</span>}
                    </TableCell>
                    <TableCell className="text-end">{row.tasksCompleted}</TableCell>
                    <TableCell className="text-end">{row.notes}</TableCell>
                    <TableCell className="text-end">{row.leadsCreated}</TableCell>
                    <TableCell className="text-end">{row.dealsWon}</TableCell>
                    <TableCell className="text-end font-semibold">{formatMoney(row.revenue, data?.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {settingTargets && <TargetsDialog period={period} label={options.find((option) => option.key === period)?.label ?? period} onClose={() => setSettingTargets(false)} />}
    </div>
  );
}

function TargetsDialog({ period, label, onClose }: { period: string; label: string; onClose: () => void }) {
  const { data } = useQuotas(period, true);
  const save = useSaveQuotas();
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!data) return;
    setValues(Object.fromEntries(data.quotas.map((quota) => [`${quota.userId}:${quota.metric}`, quota.target])));
  }, [data]);

  const submit = () => {
    if (!data) return;
    const items = data.users.flatMap((user) =>
      QUOTA_METRICS.map(([metric]) => ({ userId: user.id, metric, target: (values[`${user.id}:${metric}`] ?? '').trim() })),
    );
    save.mutate(
      { period, items },
      {
        onSuccess: () => {
          toast.success('Targets saved');
          onClose();
        },
        onError: (error) => toast.error(error instanceof ApiRequestError ? error.message : 'Could not save'),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Targets for {label}</DialogTitle>
          <DialogDescription>Leave a box empty for no target. Revenue is in the workspace currency.</DialogDescription>
        </DialogHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                {QUOTA_METRICS.map(([metric, metricLabel]) => (
                  <TableHead key={metric}>{metricLabel}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.users ?? []).map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="whitespace-nowrap font-medium">{user.name}</TableCell>
                  {QUOTA_METRICS.map(([metric]) => (
                    <TableCell key={metric}>
                      <Input
                        className="h-8 w-28"
                        inputMode="decimal"
                        value={values[`${user.id}:${metric}`] ?? ''}
                        onChange={(event) => setValues((current) => ({ ...current, [`${user.id}:${metric}`]: event.target.value }))}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || !data}>
            Save targets
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
